import { AxiosRequestConfig } from 'axios';

let controller = new AbortController();

/**
 * Attaches the shared abort signal to a request that did not bring its own, so
 * it can be cancelled centrally. Requests that set their own `signal` (e.g.
 * Inertia visits) are left untouched and manage their own lifecycle.
 */
export const attachInflightSignal = (config: AxiosRequestConfig): void => {
    if (!config.signal) {
        config.signal = controller.signal;
    }
};

/**
 * Aborts every in-flight request using the shared signal. Used on logout so
 * pending calls don't 401 and spam error toasts (and trigger redundant login
 * redirects) once the session is gone.
 *
 * This deliberately does NOT arm a fresh controller: the shared signal stays
 * aborted until {@link rearmInflightRequests} is called. That keeps the whole
 * logout teardown window covered, so stragglers dispatched after the abort
 * (e.g. the tail of a sequential fetch chain whose earlier awaits swallowed
 * their own cancellation) are aborted too instead of slipping through on a
 * fresh controller and 401-ing against the dead session.
 */
export const cancelInflightRequests = (): void => {
    controller.abort();
};

/**
 * Arms a fresh controller so subsequent requests are cancellable again. Called
 * once the post-logout navigation has landed, i.e. after the teardown window
 * has closed.
 */
export const rearmInflightRequests = (): void => {
    controller = new AbortController();
};
